import React, { useEffect, useState } from 'react';

import server from '../../../api/server';
import Badge from '../ui/Badge';
import { ExternalLinkIcon } from '../ui/icons';
import { generateConsentDocument } from './consentDocument';

// Redesign of admin/ConsentPDFExport.js — the /consent/versions and
// /consent/export requests are ported verbatim; only the version list is
// restyled. The printable document itself now lives in consentDocument.js so
// PrivacyConsentCard can render every applicant into a single record.
const formatSnapshotDate = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
};

const ConsentExport = ({ userId }) => {
    const [loadingVersions, setLoadingVersions] = useState(true);
    const [error, setError] = useState(null);
    const [versions, setVersions] = useState(null);
    const [exportingId, setExportingId] = useState(null);

    useEffect(() => {
        const loadVersions = async () => {
            if (!userId) {
                setLoadingVersions(false);
                setVersions([]);
                return;
            }
            try {
                const response = await server.post(`/consent/versions/${userId}`);
                setVersions(response.data.versions || []);
                setLoadingVersions(false);
            } catch (err) {
                console.error('Error loading consent versions:', err);
                setLoadingVersions(false);
                setVersions([]);
                setError('Failed to load consent history');
            }
        };
        loadVersions();
    }, [userId]);

    const exportConsentData = async (versionId) => {
        setExportingId(versionId || 'latest');
        setError(null);
        try {
            const response = await server.post(`/consent/export/${userId}`, {
                version_id: versionId || undefined,
            });
            const data = response.data;
            generateConsentDocument(
                [{ user: data.user, version: data.version, versions_count: data.versions_count }],
                { exported_at: data.exported_at, exported_by: data.exported_by }
            );
        } catch (err) {
            console.error('Export error:', err);
            setError(err.response?.data?.error || 'Failed to export consent data');
        } finally {
            setExportingId(null);
        }
    };

    return (
        <div>
            <ul className="divide-y divide-[#F0F1F5] rounded-xl border border-[#F0F1F5]">
                {loadingVersions ? (
                    <li className="px-4 py-3 text-sm text-[#6B7280]">Loading versions...</li>
                ) : null}

                {!loadingVersions && (!versions || versions.length === 0) ? (
                    <li className="px-4 py-3 text-sm text-[#6B7280]">No consent records.</li>
                ) : null}

                {!loadingVersions &&
                    (versions || []).map((v, i) => (
                        <li key={v.version_id} className="flex items-center justify-between gap-3 px-4 py-3">
                            <div className="min-w-0">
                                <p className="text-sm font-semibold text-[#1F2937] flex items-center gap-2 flex-wrap">
                                    {formatSnapshotDate(v.submitted_at)}
                                    {i === 0 ? <Badge tone="accent">Latest</Badge> : null}
                                    {v.expired ? <Badge tone="error">Expired</Badge> : null}
                                </p>
                                <p className="text-xs text-[#6B7280]">
                                    Expires {formatSnapshotDate(v.expires_at)} · Credit check:{' '}
                                    {v.credit_check ? v.credit_check.toUpperCase() : 'N/A'}
                                </p>
                            </div>
                            <button
                                type="button"
                                title="Export / save as PDF"
                                disabled={exportingId === v.version_id}
                                onClick={() => exportConsentData(v.version_id)}
                                className="inline-flex items-center justify-center rounded-lg p-2 text-[#6B7280] hover:bg-[#EFEAFE] hover:text-[#5B3FE0] transition-colors disabled:opacity-50"
                            >
                                <ExternalLinkIcon className="h-4 w-4" />
                            </button>
                        </li>
                    ))}
            </ul>

            {error && <p className="mt-2 text-sm text-[#DC2626]">{error}</p>}
        </div>
    );
};

export default ConsentExport;
